import { useMsal } from '@azure/msal-react';
import {
  Badge,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  makeStyles,
  useTheme,
  withStyles
} from '@material-ui/core/styles';
import {
  AppsRounded,
  ClearRounded,
  KeyboardArrowLeftRounded,
  LaunchRounded
} from '@material-ui/icons';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink, Link as RRLink } from 'react-router-dom';
import { RootState } from '../../app/store';
import plugAndPlayConfig from '../../config/plugAndPlay';
import { DRAWER_WIDTH } from '../../constants';
import { getTranslatedAllMenu, getTranslatedSystemMenu } from '../../constants/menuTabs';
import { convertDateTime } from '../../functions/convertDateTime';
import { useAppDispatch } from '../../hooks/useAppRudux';
import useDeviceScreen from '../../hooks/useDeviceScreen';
import { signOut } from '../../pages/Auth/authSCG';

const StyledBadge = withStyles((theme: Theme) =>
  createStyles({
    badge: {
      right: -24,
      top: 16,
      border: `2px solid ${theme.palette.background.paper}`,
      padding: '9px 6px',
      borderRadius: '16px'
    }
  })
)(Badge);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      backgroundColor: theme.palette.menu?.main
    },
    drawer: {
      '& .MuiDrawer-paper': {
        boxSizing: 'border-box',
        borderRadius: '0 16px 16px 0',
        [theme.breakpoints.down('xs')]: {
          borderRadius: '0 0 16px 16px'
        }
      }
    },
    list: {
      width: DRAWER_WIDTH
    },
    fullList: {
      width: 'auto'
    },
    drawerHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(0, 1),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar
    },
    isActive: {
      backgroundColor: '#edf4fe'
    },
    isActiveDarkMode: {
      backgroundColor: '#204a74'
    },
    appName: {
      marginLeft: theme.spacing(1.5),
      fontFamily: 'Comfortaa',
      fontSize: '1.9rem',
      fontWeight: 700,
      letterSpacing: -2,
      [theme.breakpoints.down('xs')]: {
        fontSize: '1.6rem',
        letterSpacing: -1.5
      },
      cursor: 'pointer',
      color: theme.palette.text.primary,
      textDecoration: 'none'
    },
    signOut: {
      color: theme.palette.error.main,
      textAlign: 'right'
    },
    exp: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      textAlign: 'right'
    },
    nested: {
      paddingLeft: theme.spacing(4)
    },
    mr1: {
      marginRight: theme.spacing(1)
    }
  })
);

interface MenuProps {
  openDrawer: boolean;
  onCloseDrawer: (event: React.KeyboardEvent | React.MouseEvent) => void;
}

export default function Menu({ openDrawer, onCloseDrawer }: MenuProps) {
  const classes = useStyles();
  const { instance } = useMsal();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { IS_MOBILE } = useDeviceScreen();
  const { t } = useTranslation();

  const { user, exp } = useSelector((state: RootState) => state.userProfile);
  
  const translatedAllMenu = getTranslatedAllMenu(t);
  const translatedSystemMenu = getTranslatedSystemMenu(t);

  function list(anchor: 'top' | 'left') {
    return (
      <div
        className={clsx(classes.list, {
          [classes.fullList]: anchor === 'top'
        })}
        role="presentation"
      >
        <div className={classes.drawerHeader}>
          <RRLink className={classes.appName} to="/" onClick={onCloseDrawer}>
            backspace
          </RRLink>
          <Tooltip title={t('sideMenu.close') as string}>
            <IconButton
              color="inherit"
              aria-label={t('sideMenu.closeAriaLabel') as string}
              onClick={onCloseDrawer}
            >
              {anchor === 'top' ? (
                <ClearRounded />
              ) : (
                <KeyboardArrowLeftRounded />
              )}
            </IconButton>
          </Tooltip>
        </div>
        <Divider />
        <List>
          {plugAndPlayConfig.filterMenuItems(translatedAllMenu).filter(
            (menu) =>
              menu.isActive &&
              (!menu.permission ||
                user?.permissions.some(
                  (permission) => permission === menu.permission
                ))
          ).map(({ label, to, icon, noti }, index) => (
            <ListItem
              component={NavLink}
              to={to}
              button
              key={index}
              onClick={onCloseDrawer}
              activeClassName={
                theme.palette.type === 'dark'
                  ? classes.isActiveDarkMode
                  : classes.isActive
              }
            >
              <ListItemIcon>{icon}</ListItemIcon>
              <StyledBadge badgeContent={noti} color="secondary">
                <ListItemText primary={label} />
              </StyledBadge>
            </ListItem>
          ))}
        </List>
        <Divider />
        <List>
          {translatedSystemMenu.filter((menu) => menu.isActive).map(
            ({ label, to }, index) => (
              <ListItem
                component={NavLink}
                to={to}
                button
                key={index}
                onClick={onCloseDrawer}
                activeClassName={
                  theme.palette.type === 'dark'
                    ? classes.isActiveDarkMode
                    : classes.isActive
                }
              >
                <ListItemText primary={label} />
              </ListItem>
            )
          )}
          <ListItem
            button
            onClick={() => {
              window.open(
                `${process.env.REACT_APP_REQUEST_FEATURE_WEB_URL}?systemName=doc`,
                '_blank'
              );
            }}
          >
            <ListItemText primary={t('sideMenu.feedback')} />
          </ListItem>
          <ListItem
            button
            onClick={() => {
              window.open(
                `${process.env.REACT_APP_HOME_PAGE_WEB_URL}/account`,
                '_blank'
              );
            }}
          >
            <AppsRounded className={classes.mr1} color="action" />
            <ListItemText primary={t('sideMenu.services')} />
            <LaunchRounded color="action" fontSize="small" />
          </ListItem>
          <ListItem button>
            <ListItemText
              className={classes.signOut}
              onClick={() => signOut({ dispatch, instance })}
              primary={t('sideMenu.logOut')}
            />
          </ListItem>
          <div className={classes.exp}>
            <Typography component="p" variant="caption" color="textSecondary">
              {t('sideMenu.sessionExpiry')}
            </Typography>
            <Typography component="i" variant="body2">
              {convertDateTime(exp)}
            </Typography>
          </div>
        </List>
      </div>
    );
  }

  return (
    <Drawer
      className={classes.drawer}
      classes={{ paper: classes.paper }}
      anchor={IS_MOBILE ? 'top' : 'left'}
      open={openDrawer}
      onClose={onCloseDrawer}
    >
      {list(IS_MOBILE ? 'top' : 'left')}
    </Drawer>
  );
}
